import Color from 'colorjs.io';

/**
 * @typedef {Object} RouteData
 * @property {[number, number]} charge - Starting charge and ending charge in percentage.
 * @property {Array.<RouteSegment>} segments - Array of segments.
 */

/**
 * @typedef {Object} RouteSegment
 * @property {Coordinates} start - Starting coordinates of the segment.
 * @property {Coordinates} end - Ending coordinates of the segment.
 * @property {number} duration - Duration of the segment in seconds.
 */

/**
 * @typedef {Array.<number>} Coordinates
 * @property {number} 0 - Longitude of the coordinate.
 * @property {number} 1 - Latitude of the coordinate.
 */

function getGradientStops(charge) {
  const black = new Color(Color.spaces.hsl, [0, 0, 0]);
  const color = new Color('#ff0000'); // Red
  const gradient = color.range('#00ff00', {
    space: 'hsl',
    outputSpace: 'hsl',
  });

  const startColor = charge[0] > 0 ? gradient(charge[0]).hsl : black.hsl;
  const endColor = charge[1] > 0 && charge[1] < 100 ? gradient(charge[1]).hsl : black.hsl;

  return [
    [0, ['hsl', ...startColor]],
    [1, ['hsl', ...endColor]],
  ].flat();
}

export default class MapRoutes {
  constructor(parent) {
    this.parent = parent; // Reference to the map web component
    this.map = parent.map;
    /**
     * Object containing routes.
     * @type {Object.<string, RouteData>}
     */
    this.routes = {
      default: [],
    };
  }

  add(routes, source = 'default') {
    if (routes) {
      this.routes[source] = routes;
    }
    const activeRoute = this.routes[source];
    if (activeRoute.segments.length) {
      const points = activeRoute.segments.flatMap((segment) => [segment.start, segment.end]);
      this.map.addSource(`route-${source}`, {
        type: 'geojson',
        lineMetrics: true,
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: points,
          },
        },
      });

      // Check if this is an order connection line (charge is [0, 0])
      const isOrderConnection = activeRoute.charge[0] === 0 && activeRoute.charge[1] === 0;
      const lineWidth = isOrderConnection ? 1.5 : 3;
      const outlineWidth = isOrderConnection ? 3 : 5;
      const lineOffset = isOrderConnection ? 0 : 3;

      // Outline the main line
      this.map.addLayer({
        id: `route-outline-${source}`,
        type: 'line',
        source: `route-${source}`,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': 'black', // Outline color
          'line-width': outlineWidth, // Outline width
          'line-opacity': 0.6, // Outline opacity
          'line-offset': lineOffset, // Outline offset
        },
      });

      // Main line layer
      this.map.addLayer({
        id: `route-${source}`,
        type: 'line',
        source: `route-${source}`,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#000',
          'line-width': lineWidth,
          'line-offset': lineOffset,
          'line-gradient': ['interpolate', ['linear'], ['line-progress'], ...getGradientStops(activeRoute.charge)],
        },
      });
    }
  }

  clear(source = 'default') {
    if (!this.map?.style) return;

    if (this.map.getLayer(`route-${source}`)) {
      this.map.removeLayer(`route-${source}`);
    }
    if (this.map.getLayer(`route-outline-${source}`)) {
      this.map.removeLayer(`route-outline-${source}`);
    }
    if (this.map.getSource(`route-${source}`)) {
      this.map.removeSource(`route-${source}`);
    }
  }

  clearAll() {
    for (let source in this.routes) {
      this.clear(source);
    }
  }
}
